import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import en from './locales/en';
import ar from './locales/ar';
import zh from './locales/zh';
import ms from './locales/ms';

const LOCALES = { en, ar, zh, ms };

export const LANGUAGES = [
  { code: 'en', name: 'English',         nativeName: 'English',         flag: '🇬🇧' },
  { code: 'ar', name: 'Arabic',          nativeName: 'العربية',         flag: '🇸🇦' },
  { code: 'zh', name: 'Chinese',         nativeName: '中文',             flag: '🇨🇳' },
  { code: 'ms', name: 'Malay',           nativeName: 'Bahasa Melayu',   flag: '🇲🇾' },
];

const RTL = new Set(['ar']);
const STORAGE_KEY = 'lang';

function getInitialLang() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && LOCALES[saved]) return saved;
  const browser = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return LOCALES[browser] ? browser : 'en';
}

// Standalone lookup for non-React modules (e.g. the axios interceptor that
// localizes server error codes). Same semantics as t(): falls back to English,
// then to the key itself.
export function translate(key, vars) {
  const lang = getInitialLang();
  const lookup = dict => key.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), dict);
  let value = lookup(LOCALES[lang]);
  if (value == null && lang !== 'en') value = lookup(LOCALES.en);
  if (value == null) return key;
  if (vars && typeof value === 'string') {
    return value.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : `{${k}}`));
  }
  return value;
}

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(getInitialLang);

  // Apply lang + direction to <html>
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL.has(lang) ? 'rtl' : 'ltr';
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const setLang = useCallback(c => { if (LOCALES[c]) setLangState(c); }, []);

  // t('hero.headline1') → walks the dict; falls back to English; finally returns the key.
  const t = useCallback((key, vars) => {
    const lookup = dict => key.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), dict);
    let value = lookup(LOCALES[lang]);
    if (value == null && lang !== 'en') value = lookup(LOCALES.en);
    if (value == null) return key;
    if (vars && typeof value === 'string') {
      return value.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : `{${k}}`));
    }
    return value;
  }, [lang]);

  const isRTL = RTL.has(lang);

  return (
    <LangContext.Provider value={{ lang, setLang, t, isRTL, languages: LANGUAGES }}>
      {children}
    </LangContext.Provider>
  );
}

export const useT = () => useContext(LangContext);
